import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Check } from "lucide-react";

const empty = { name: "", region: "", lng: 0, lat: 0, summary: "", impact: "", can_join: true, order: 99 };

export default function ProjectsManager({ projects, reload }) {
    const [editing, setEditing] = useState(null); // project id or "new"
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const startNew = () => { setForm(empty); setEditing("new"); };
    const startEdit = (p) => {
        setForm({ ...p, impact: (p.impact || []).join(", ") });
        setEditing(p.id);
    };
    const cancel = () => { setEditing(null); setForm(empty); };

    const change = (k) => (e) => {
        const v = k === "can_join" ? e.target.checked : e.target.value;
        setForm({ ...form, [k]: v });
    };

    const save = async () => {
        setSaving(true);
        const payload = {
            name: form.name,
            region: form.region,
            lng: parseFloat(form.lng) || 0,
            lat: parseFloat(form.lat) || 0,
            summary: form.summary,
            impact: String(form.impact).split(",").map((s) => s.trim()).filter(Boolean),
            can_join: !!form.can_join,
            order: parseInt(form.order) || 99,
        };
        try {
            if (editing === "new") await api.post("/admin/projects", payload);
            else await api.put(`/admin/projects/${editing}`, payload);
            toast.success("Project saved");
            cancel();
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"? This removes it from the map.`)) return;
        try {
            await api.delete(`/admin/projects/${p.id}`);
            toast.success("Project deleted");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="projects-manager">
            <div className="flex items-center justify-between mb-6">
                <div className="label-mono text-[var(--ivory)]/60">{projects.length} MAP PROJECTS</div>
                <button className="btn-gold" onClick={startNew} data-testid="project-add-btn"><Plus className="w-4 h-4" /> Add region</button>
            </div>

            {editing && (
                <div className="border border-[var(--gold)] p-6 mb-8 bg-[var(--navy-deep)]" data-testid="project-editor">
                    <div className="flex items-center justify-between mb-6">
                        <div className="label-mono text-[var(--gold)]">{editing === "new" ? "— NEW REGION" : "— EDIT REGION"}</div>
                        <button onClick={cancel} className="text-[var(--ivory)]/60 hover:text-[var(--ivory)]"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <div><label className="label-mono block mb-2">Name</label><input className="input-editorial" value={form.name} onChange={change("name")} data-testid="project-name" /></div>
                        <div><label className="label-mono block mb-2">Region label</label><input className="input-editorial" value={form.region} onChange={change("region")} data-testid="project-region" /></div>
                        <div><label className="label-mono block mb-2">Longitude</label><input type="number" step="any" className="input-editorial" value={form.lng} onChange={change("lng")} data-testid="project-lng" /></div>
                        <div><label className="label-mono block mb-2">Latitude</label><input type="number" step="any" className="input-editorial" value={form.lat} onChange={change("lat")} data-testid="project-lat" /></div>
                        <div className="md:col-span-2"><label className="label-mono block mb-2">Summary</label><textarea rows={3} className="input-editorial resize-none" value={form.summary} onChange={change("summary")} data-testid="project-summary" /></div>
                        <div className="md:col-span-2"><label className="label-mono block mb-2">Impact tags (comma separated)</label><input className="input-editorial" value={form.impact} onChange={change("impact")} data-testid="project-impact" placeholder="Youth fellowship, NGO partners" /></div>
                        <div><label className="label-mono block mb-2">Order</label><input type="number" className="input-editorial" value={form.order} onChange={change("order")} data-testid="project-order" /></div>
                        <label className="flex items-center gap-3 mt-8 cursor-pointer">
                            <input type="checkbox" checked={!!form.can_join} onChange={change("can_join")} data-testid="project-canjoin" className="w-4 h-4 accent-[var(--gold)]" />
                            <span className="label-mono text-[var(--ivory)]/80">Open to join</span>
                        </label>
                    </div>
                    <button className="btn-gold mt-6" onClick={save} disabled={saving} data-testid="project-save"><Check className="w-4 h-4" /> {saving ? "Saving…" : "Save region"}</button>
                </div>
            )}

            <div className="border-t border-l border-[rgba(255,255,240,0.12)]">
                {projects.map((p) => (
                    <div key={p.id} className="grid grid-cols-12 border-r border-b border-[rgba(255,255,240,0.12)]" data-testid={`project-row-${p.slug}`}>
                        <div className="col-span-1 p-4 label-mono text-[var(--gold)]">{p.order}</div>
                        <div className="col-span-5 md:col-span-4 p-4">
                            <div className="font-serif text-lg">{p.name}</div>
                            <div className="label-mono text-[var(--ivory)]/50 mt-1">{p.region}</div>
                        </div>
                        <div className="col-span-3 md:col-span-4 p-4 hidden md:block text-sm text-[var(--ivory)]/60 truncate">{p.summary}</div>
                        <div className="col-span-3 md:col-span-1 p-4 label-mono">
                            {p.can_join ? <span className="text-[var(--gold)]">JOIN</span> : <span className="text-[#4FB07C]">SOON</span>}
                        </div>
                        <div className="col-span-3 md:col-span-2 p-4 flex items-center gap-3 justify-end">
                            <button onClick={() => startEdit(p)} className="text-[var(--ivory)]/60 hover:text-[var(--gold)]" data-testid={`project-edit-${p.slug}`}><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => remove(p)} className="text-[var(--ivory)]/60 hover:text-[#e07a7a]" data-testid={`project-delete-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
